import Cookies from "js-cookie";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SupportStyleTable from "../../Components/Table/SupportStyleTable";
import {
  fetchInhouseRndData,
  findSupport,
  InhouseRNDCompleteTask,
  findJuniorDevelopers,
  InhousRNDAcceptTask,
  SeniortoJuniorAssignReq,
  JuniorToSeniorAssignRatingReq,
  SupportHoldTask,
  updateCallNo,
  checkingAfterImplementationReq,
} from "../../Redux/Slice/TaskMasterSlice";
import Loader from "../../Components/Loader/Loader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import socket from "../../socket";
import {
  UserPlus,
  X,
  Clock,
  CheckCircle2,
  Calendar,
  Eye,
  Pencil,
  CheckCheck,
} from "lucide-react";
import { createPortal } from "react-dom";
import "./InhouseRnd.css";

const formatDateOnly = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);

  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
};

const calculateWorkingSecondsStrict = (startTime, endTime) => {
  let start = new Date(startTime);
  let end = new Date(endTime);
  if (end < start) return 0;

  let totalSeconds = 0;

  let currentDay = new Date(start);
  currentDay.setHours(0, 0, 0, 0);

  let endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);

  while (currentDay <= endDay) {
    if (currentDay.getDay() !== 0) { // Not Sunday
      let dayStart = new Date(currentDay); dayStart.setHours(10, 0, 0, 0);
      let dayEnd = new Date(currentDay); dayEnd.setHours(19, 0, 0, 0);
      let lunchStart = new Date(currentDay); lunchStart.setHours(13, 30, 0, 0);
      let lunchEnd = new Date(currentDay); lunchEnd.setHours(14, 0, 0, 0);

      let workStart = Math.max(start.getTime(), dayStart.getTime());
      let workEnd = Math.min(end.getTime(), dayEnd.getTime());

      if (workStart < workEnd && workStart < dayEnd.getTime() && workEnd > dayStart.getTime()) {
        let overlap = workEnd - workStart;

        let lStart = Math.max(workStart, lunchStart.getTime());
        let lEnd = Math.min(workEnd, lunchEnd.getTime());

        if (lStart < lEnd) {
          overlap -= (lEnd - lStart);
        }

        totalSeconds += overlap / 1000;
      }
    }
    currentDay.setDate(currentDay.getDate() + 1);
  }
  return Math.floor(totalSeconds);
};

const getFormatForRemainingTime = (timeLimitStr, slabRecords, isHold = false, fallbackDate = null) => {
  if (!timeLimitStr) {
    return { taskWorkTime: "NaN", calcDays: "NaN", estimatedDateStr: "NaN" };
  }

  let allowedSeconds = 0;
  if (timeLimitStr) {
    const parts = timeLimitStr.split(':');
    allowedSeconds = ((parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0)) * 60;
  }

  let workedSeconds = 0;
  if (slabRecords && Array.isArray(slabRecords) && slabRecords.length > 0) {
    const sorted = [...slabRecords].sort((a, b) => new Date(a.Date) - new Date(b.Date));
    let acceptTime = null;
    if (sorted[0].type === "Hold" && fallbackDate) {
      acceptTime = new Date(fallbackDate);
    }
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].type === "Accept") {
        acceptTime = new Date(sorted[i].Date);
      } else if (sorted[i].type === "Hold" && acceptTime) {
        workedSeconds += calculateWorkingSecondsStrict(acceptTime, new Date(sorted[i].Date));
        acceptTime = null;
      }
    }
    if (acceptTime) {
      workedSeconds += calculateWorkingSecondsStrict(acceptTime, new Date());
    }
  } else if (!isHold && fallbackDate) {
    workedSeconds += calculateWorkingSecondsStrict(new Date(fallbackDate), new Date());
  }

  let remainingSec = allowedSeconds - workedSeconds;
  let isNeg = remainingSec < 0;
  remainingSec = Math.abs(remainingSec);

  let days = Math.floor(remainingSec / (510 * 60));
  let leftOverSecsWhole = remainingSec % (510 * 60);
  let leftHours = Math.floor(leftOverSecsWhole / 3600);
  let leftMins = Math.floor((leftOverSecsWhole % 3600) / 60);
  let leftSecs = Math.floor(leftOverSecsWhole % 60);

  let calcDays = `${isNeg && days > 0 ? "-" : ""}${days} Day`;
  if (!isNeg) {
    if (days === 0) calcDays = "Today";
    else if (days === 1) calcDays = "Tommorow";
  }
  let taskWorkTime = `${isNeg ? "- " : ""}${leftHours} Hrs ${leftMins} Mins ${leftSecs} Sec`;

  let estimatedDateStr = "Overdue";
  if (!isNeg) {
    let estDate = new Date();
    let daysToAdd = days;
    while (daysToAdd > 0) {
      estDate.setDate(estDate.getDate() + 1);
      if (estDate.getDay() !== 0) daysToAdd--;
    }
    if (days === 0 && estDate.getDay() === 0) {
      estDate.setDate(estDate.getDate() + 1);
    }
    estimatedDateStr = formatDateOnly(estDate);
  }

  return { taskWorkTime, calcDays, estimatedDateStr, isNeg };
};

const LiveTimeCell = ({ timeLimitStr, slabRecords, isHold, type, fallbackDate }) => {
  const [result, setResult] = useState(getFormatForRemainingTime(timeLimitStr, slabRecords, isHold, fallbackDate));

  useEffect(() => {
    const interval = setInterval(() => {
      setResult(getFormatForRemainingTime(timeLimitStr, slabRecords, isHold, fallbackDate));
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLimitStr, slabRecords, isHold, fallbackDate]);

  if (type === "estimatedDateStr") {
    return (
      <span className="font-semibold text-slate-700 text-xs">
        {result.estimatedDateStr === "NaN" ? "--" : result.estimatedDateStr}
      </span>
    );
  }
  if (type === "calcDays") {
    const isNeg = result.isNeg;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
          isNeg
            ? "bg-red-50 text-red-700 border border-red-200"
            : "bg-blue-50 text-blue-700 border border-blue-200"
        }`}
      >
        {result.calcDays === "NaN" ? "--" : result.calcDays}
      </span>
    );
  }
  if (type === "taskWorkTime") {
    if (!timeLimitStr || result.taskWorkTime === "NaN") return <span className="text-slate-400">--</span>;
    const badgeStyle = isHold
      ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
      : result.isNeg
      ? "bg-red-50 text-red-700 border border-red-200"
      : "bg-emerald-50 text-emerald-700 border border-emerald-200";

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-mono font-bold ${badgeStyle}`}
      >
        <Clock size={11} />
        <span>{result.taskWorkTime}</span>
      </span>
    );
  }

  if (timeLimitStr && type === "formattedTime") {
    const parts = timeLimitStr.split(':');
    if (parts.length === 2) {
      return (
        <span className="text-xs font-semibold text-slate-700 font-mono">
          {`${parts[0]}h : ${parts[1]}m`}
        </span>
      );
    }
  }

  return <span className="text-slate-700 text-xs">{timeLimitStr || "--"}</span>;
};

// Modern Dynamic Gradients for Company Avatars
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #4f46e5, #7c3aed)",
  "linear-gradient(135deg, #059669, #10b981)",
  "linear-gradient(135deg, #e11d48, #f43f5e)",
  "linear-gradient(135deg, #d97706, #f59e0b)",
  "linear-gradient(135deg, #0284c7, #06b6d4)",
  "linear-gradient(135deg, #9333ea, #c026d3)",
  "linear-gradient(135deg, #0d9488, #14b8a6)",
  "linear-gradient(135deg, #ea580c, #fb923c)",
  "linear-gradient(135deg, #2563eb, #38bdf8)",
  "linear-gradient(135deg, #db2777, #f472b6)",
  "linear-gradient(135deg, #0891b2, #22d3ee)",
  "linear-gradient(135deg, #16a34a, #4ade80)",
];

const getAvatarGradient = (str) => {
  if (!str || typeof str !== "string") return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[index];
};

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "🏢";
  const clean = name.trim().replace(/[^a-zA-Z0-9\s]/g, "");
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const first = words[0][0] || "";
    const second = words[1][0] || "";
    return (first + second).toUpperCase();
  }
  if (clean.length >= 2) {
    return clean.slice(0, 2).toUpperCase();
  }
  return clean ? clean.toUpperCase() : "🏢";
};

const DeveloperAvatar = ({ photo, name, size = "w-8 h-8" }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [photo]);

  if (photo && String(photo).trim() !== "" && !hasError) {
    return (
      <img
        src={photo}
        alt={name || "Developer"}
        className={`${size} rounded-full object-cover shrink-0 border border-slate-200 shadow-xs`}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full shrink-0 border border-slate-200 bg-blue-100 text-[#112A47] flex items-center justify-center text-[11px] font-bold tracking-tight uppercase shadow-xs`}
    >
      {getInitials(name)}
    </div>
  );
};

const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(
    (opt) =>
      (opt.label || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (opt.Role || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = (options || []).find((opt) => String(opt._id) === String(value));

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center justify-between">
        <span>{placeholder}</span>
        <span className="text-red-500 font-bold">*</span>
      </label>
      <div
        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl cursor-pointer flex justify-between items-center hover:border-[#112A47] focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 transition-all shadow-sm min-h-[44px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {selectedOption ? (
            <>
              <DeveloperAvatar photo={selectedOption.ProfilePhoto} name={selectedOption.label} size="w-7 h-7" />
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="text-slate-800 font-semibold text-sm truncate">
                  {selectedOption.label}
                </span>
                {selectedOption.isPresent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Present
                  </span>
                )}
              </div>
            </>
          ) : (
            <span className="text-slate-400 text-sm">Choose developer...</span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180 text-[#112A47]" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-slate-100 bg-slate-50/50">
            <input
              type="text"
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-400"
              placeholder={`Search developer...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          <div className="max-h-56 overflow-y-auto p-1 divide-y divide-slate-50">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt._id}
                  className={`flex items-center justify-between gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors rounded-lg my-0.5 ${
                    String(value) === String(opt._id)
                      ? "bg-blue-50 text-[#112A47] font-semibold"
                      : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                  }`}
                  onClick={() => {
                    onChange(opt._id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <DeveloperAvatar photo={opt.ProfilePhoto} name={opt.label} size="w-8 h-8" />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate leading-tight font-medium text-slate-800 text-xs">
                        {opt.label}
                      </span>
                      {opt.Role && (
                        <span className="text-[10px] text-slate-400 font-normal leading-tight truncate">
                          {opt.Role}
                        </span>
                      )}
                    </div>
                  </div>

                  {opt.isPresent && (
                    <div className="flex items-center shrink-0 pl-1">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        Present
                      </span>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-4 text-xs text-slate-400 text-center italic">
                No developers found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const InhouseRnd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Active Tab state persisted in sessionStorage (excluding 'all')
  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem("inhouseRndActiveTab");
    return saved && saved !== "all" ? saved : "queue";
  });

  useEffect(() => {
    sessionStorage.setItem("inhouseRndActiveTab", activeTab);
  }, [activeTab]);

  // Live Current Date & Time Clock
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatLiveTime = (d) => {
    return d
      .toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
      .toUpperCase();
  };

  const formatLiveDate = (d) => {
    return d.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  let parsedData = null;
  try {
    const raw = Cookies.get("loginData");
    if (raw) parsedData = JSON.parse(raw);
  } catch (e) {
    console.error("Error parsing loginData:", e);
  }

  const loggedInUserId = parsedData?.data?._id || parsedData?.user?._id || parsedData?._id;

  const { InhousRndData, JuniorDevelopers, DataFindSupport } = useSelector(
    (state) => state.TaskMaster
  );

  useEffect(() => {
    const handleUpdate = (data) => {
      console.log("Live inhouse RND update received:", data);
      if (loggedInUserId) {
        dispatch(fetchInhouseRndData(loggedInUserId));
      }
    };
    socket.on("customerReqUpdate", handleUpdate);

    return () => {
      socket.off("customerReqUpdate", handleUpdate);
    };
  }, [dispatch, loggedInUserId]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isJuniorModalOpen, setIsJuniorModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [recordData, setRecordData] = useState([]);
  const [selectedJuniorId, setSelectedJuniorId] = useState("");
  const [selectedSeniorId, setSelectedSeniorId] = useState("");
  const [isJuniorToSeniorModalOpen, setIsJuniorToSeniorModalOpen] = useState(false);
  const [isCheckTaskModalOpen, setIsCheckTaskModalOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [timeLimitHours, setTimeLimitHours] = useState("");
  const [timeLimitMinutes, setTimeLimitMinutes] = useState("");
  const [minutesError, setMinutesError] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);
  const [editCallNo, setEditCallNo] = useState("");
  const [editPresentStatus, setEditPresentStatus] = useState("");
  const [editRequirement, setEditRequirement] = useState("");
  const [editSolution, setEditSolution] = useState("");
  const [editBenefit, setEditBenefit] = useState("");

  const [isPostCheckModalOpen, setIsPostCheckModalOpen] = useState(false);
  const [selectedPostCheckMemberId, setSelectedPostCheckMemberId] = useState("");
  const [postCheckDescription, setPostCheckDescription] = useState("");
  const [postCheckCodeUpdate, setPostCheckCodeUpdate] = useState("");
  const [postCheckTaskId, setPostCheckTaskId] = useState(null);
  const [postCheckTaskToken, setPostCheckTaskToken] = useState("");

  useEffect(() => {
    if (loggedInUserId) {
      dispatch(fetchInhouseRndData(loggedInUserId));
    }
    dispatch(findSupport());
    dispatch(findJuniorDevelopers());
  }, [dispatch, loggedInUserId]);

  const handleSaveEdit = async () => {
    try {
      const resultAction = await dispatch(updateCallNo({
        taskId: editTaskId,
        PresentStatus: editPresentStatus,
        Requirement: editRequirement,
        Solution: editSolution,
        Benefit: editBenefit
      }));
      if (updateCallNo.fulfilled.match(resultAction)) {
        toast.success("Task details updated successfully!");
        setIsEditModalOpen(false);
        dispatch(fetchInhouseRndData(loggedInUserId));
      } else {
        toast.error(resultAction.payload || "Failed to update task details.");
      }
    } catch (error) {
      console.error("Error updating task details:", error);
      toast.error(error.message || "An error occurred.");
    }
  };

  const SeniorOptions = useMemo(() => {
    const map = new Map();
    const seenNames = new Set();

    const addMember = (m, defaultRole) => {
      if (!m || !m._id) return;
      if (loggedInUserId && String(m._id) === String(loggedInUserId)) return;
      if (m.Profile && String(m.Profile).toLowerCase() === "off") return;
      if (m.profile && String(m.profile).toLowerCase() === "off") return;
      if (m.Status && String(m.Status).toLowerCase() === "off") return;
      if (m.status && String(m.status).toLowerCase() === "off") return;
      if (m.isActive === false || m.active === false) return;

      const role = (m.Role || defaultRole || "").trim();
      const roleLower = role.toLowerCase();
      if (roleLower.includes("owner") || roleLower.includes("management")) return;

      const isDelphiSenior =
        roleLower.includes("delphi") &&
        (roleLower.includes("senior") || roleLower.includes("lead"));
      const isSupport = roleLower.includes("support");

      if (!isDelphiSenior && !isSupport) return;

      const name = (m.Name || "").trim();
      const nameLower = name.toLowerCase();
      if (!name || nameLower.includes("dynamic management")) return;
      const nameKey = name.toLowerCase();
      const idKey = String(m._id);

      if (!map.has(idKey) && !seenNames.has(nameKey)) {
        seenNames.add(nameKey);
        map.set(idKey, {
          label: name,
          _id: m._id,
          ProfilePhoto: m.ProfilePhoto || null,
          Role: role || defaultRole,
          isPresent: Boolean(m.isPresent),
          punchInTime: m.punchInTime || null,
        });
      }
    };

    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => addMember(d, "Developer"));
    }
    if (Array.isArray(DataFindSupport?.data)) {
      DataFindSupport.data.forEach((s) => addMember(s, "Customer Support"));
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [JuniorDevelopers?.data, DataFindSupport?.data, loggedInUserId]);

  const juniorOptions = useMemo(() => {
    const map = new Map();
    const seenNames = new Set();

    const addMember = (m, defaultRole) => {
      if (!m || !m._id) return;
      if (loggedInUserId && String(m._id) === String(loggedInUserId)) return;
      if (m.Profile && String(m.Profile).toLowerCase() === "off") return;
      if (m.profile && String(m.profile).toLowerCase() === "off") return;
      if (m.Status && String(m.Status).toLowerCase() === "off") return;
      if (m.status && String(m.status).toLowerCase() === "off") return;
      if (m.isActive === false || m.active === false) return;

      const role = (m.Role || defaultRole || "").trim();
      const roleLower = role.toLowerCase();
      if (roleLower.includes("owner") || roleLower.includes("management") || roleLower.includes("support")) return;

      const name = (m.Name || "").trim();
      const nameLower = name.toLowerCase();
      if (!name || nameLower.includes("dynamic management")) return;
      const nameKey = name.toLowerCase();
      const idKey = String(m._id);

      if (!map.has(idKey) && !seenNames.has(nameKey)) {
        seenNames.add(nameKey);
        map.set(idKey, {
          label: name,
          _id: m._id,
          ProfilePhoto: m.ProfilePhoto || null,
          Role: role || defaultRole,
          isPresent: Boolean(m.isPresent),
          punchInTime: m.punchInTime || null,
        });
      }
    };

    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => addMember(d, "Developer"));
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [JuniorDevelopers?.data, loggedInUserId]);

  const postCheckMemberOptions = useMemo(() => {
    const list = [];
    if (Array.isArray(DataFindSupport?.data)) {
      DataFindSupport.data.forEach((s) => {
        if (s?.Profile && String(s.Profile).toLowerCase() === "off") return;
        if (s?.profile && String(s.profile).toLowerCase() === "off") return;
        if (s?.Status && String(s.Status).toLowerCase() === "off") return;
        if (s?.status && String(s.status).toLowerCase() === "off") return;
        if (s?.isActive === false || s?.active === false) return;
        const role = (s?.Role || "").toLowerCase();
        if (!role.includes("owner") && !role.includes("management") && !(s?.Name || "").toLowerCase().includes("dynamic management")) {
          list.push({
            label: s?.Name,
            _id: s?._id,
            ProfilePhoto: s?.ProfilePhoto,
            Role: s?.Role || "Support",
            isPresent: Boolean(s?.isPresent),
            punchInTime: s?.punchInTime,
          });
        }
      });
    }
    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => {
        if (d?.Profile && String(d.Profile).toLowerCase() === "off") return;
        if (d?.profile && String(d.profile).toLowerCase() === "off") return;
        if (d?.Status && String(d.Status).toLowerCase() === "off") return;
        if (d?.status && String(d.status).toLowerCase() === "off") return;
        if (d?.isActive === false || d?.active === false) return;
        const role = (d?.Role || "").toLowerCase();
        if (role.includes("owner") || role.includes("management") || (d?.Name || "").toLowerCase().includes("dynamic management")) return;
        if (!list.some((item) => String(item._id) === String(d._id))) {
          list.push({
            label: d?.Name,
            _id: d?._id,
            ProfilePhoto: d?.ProfilePhoto,
            Role: d?.Role || "Developer",
            isPresent: Boolean(d?.isPresent),
            punchInTime: d?.punchInTime,
          });
        }
      });
    }
    return list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [DataFindSupport?.data, JuniorDevelopers?.data]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);

    const day = date.getUTCDate().toString().padStart(2, "0");
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
    const year = date.getUTCFullYear();

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = hours.toString().padStart(2, "0");

    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const handleShowTask = (id) => {
    navigate(`/InhouseRnd/CustomerrequustDetail/${id}`);
  };

  const handleRowClick = (row) => {
    const taskId = row?.MainId || row?._id || row?.id;
    if (taskId) {
      handleShowTask(taskId);
    }
  };

  const handleAcceptTask = (tid, AssingName) => {
    if (AssingName) {
      const confirmed = window.confirm(
        `Are you sure? This task is currently assigned to ${AssingName}.`
      );

      if (!confirmed) {
        return;
      }
    }

    const userId = loggedInUserId;

    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    toast.info("Accepting task...", { autoClose: 1500 });

    dispatch(InhousRNDAcceptTask({ id: userId, tid }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Task accepted successfully!");
          setTimeout(() => {
            dispatch(fetchInhouseRndData(userId));
          }, 100);
        } else {
          toast.error("Failed to accept task. Please try again.");
        }
      })
      .catch(() => {
        toast.error("An error occurred while accepting task.");
      });
  };

  const handleHoldTask = (tid) => {
    const userId = loggedInUserId;

    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    toast.info("Holding task...", { autoClose: 1500 });

    dispatch(SupportHoldTask({ id: userId, tid }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Task put on hold successfully!");
          setTimeout(() => {
            dispatch(fetchInhouseRndData(userId));
          }, 100);
        } else {
          toast.error("Failed to hold task.");
        }
      })
      .catch(() => {
        toast.error("An error occurred while holding task.");
      });
  };

  const handleSendToPostCheck = (tid, token, defaultMemberId, existingCodeUpdate) => {
    setPostCheckTaskId(tid);
    setPostCheckTaskToken(token || "");
    setSelectedPostCheckMemberId(defaultMemberId || "");
    setPostCheckDescription("");
    setPostCheckCodeUpdate(existingCodeUpdate || "");
    setIsPostCheckModalOpen(true);
  };

  const handleStoreRecord = (recordArray) => {
    if (Array.isArray(recordArray)) {
      const uniqueDevelopers = recordArray.filter(
        (item, index, self) =>
          index ===
          self.findIndex((r) => r.InHouseId?._id === item.InHouseId?._id)
      );

      const formatted = uniqueDevelopers.map((item) => ({
        DeveloperID: item.InHouseId?._id,
        DeveloperName: item.InHouseId?.Name,
      }));

      setRecordData(formatted);
    } else {
      toast.warn("No developer records found for this task.");
    }
  };

  // --- High-Quality Cell Renderers (Support Dashboard Standards) ---
  const renderTokenCell = ({ value }) => (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full font-bold text-xs shadow-2xs"
      style={{
        backgroundColor: "#EFF6FF",
        color: "#1D4ED8",
        border: "1px solid #BFDBFE",
        letterSpacing: "0.01em",
      }}
    >
      #{value || "--"}
    </span>
  );

  const renderCompanyCell = ({ value }) => {
    const companyName = value || "--";
    const initials = getInitials(companyName);
    const bgGradient = getAvatarGradient(companyName);

    return (
      <div className="flex items-center gap-2 py-0.5">
        <span
          className="inline-flex justify-center items-center rounded-full font-bold text-white flex-shrink-0 text-[10px]"
          style={{
            width: "28px",
            height: "28px",
            minWidth: "28px",
            minHeight: "28px",
            background: bgGradient,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
            border: "1.5px solid rgba(255, 255, 255, 0.95)",
            lineHeight: 1,
          }}
        >
          {initials}
        </span>
        <span
          className="font-semibold text-slate-800 text-xs truncate max-w-[210px]"
          title={companyName}
        >
          {companyName}
        </span>
      </div>
    );
  };

  const renderDateCell = ({ value }) => {
    if (!value || value === "-") return <span className="text-slate-400">--</span>;
    const parts = String(value).split(" ");
    const dateStr = parts[0] || "--";
    const timeStr = parts.slice(1).join(" ");

    return (
      <div className="flex flex-col justify-center leading-tight whitespace-nowrap">
        <span className="text-slate-800 font-semibold inline-flex items-center gap-1 text-xs">
          <Calendar size={12} className="text-blue-600" />
          <span>{dateStr}</span>
        </span>
        {timeStr && (
          <span className="text-slate-400 inline-flex items-center gap-1 mt-0.5 text-[11px] font-mono font-medium">
            <Clock size={11} className="text-slate-400" />
            <span>{timeStr}</span>
          </span>
        )}
      </div>
    );
  };

  const renderPersonCell = ({ value }) => {
    if (!value || value === "-" || value === "null") {
      return <span className="text-slate-400 text-xs">--</span>;
    }
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 shadow-2xs whitespace-nowrap"
      >
        <span>👤</span>
        <span className="font-semibold text-slate-800">{value}</span>
      </span>
    );
  };

  const renderStatusCell = ({ value }) => {
    const status = value || "Pending";
    let bg = "#FEF3C7";
    let text = "#92400E";
    let border = "#FDE68A";
    let dot = "#D97706";

    const lower = status.toLowerCase();
    if (lower.includes("complete")) {
      bg = "#D1FAE5";
      text = "#065F46";
      border = "#A7F3D0";
      dot = "#10B981";
    } else if (lower.includes("checking after implementation")) {
      bg = "#ECFEFF";
      text = "#0E7490";
      border = "#A5F3FC";
      dot = "#06B6D4";
    } else if (lower.includes("forward for implementation") || lower.includes("implementation")) {
      bg = "#F3E8FF";
      text = "#6B21A8";
      border = "#E9D5FF";
      dot = "#A855F7";
    } else if (lower.includes("checking to support")) {
      bg = "#E0F2FE";
      text = "#0369A1";
      border = "#BAE6FD";
      dot = "#0284C7";
    } else if (lower.includes("accept") || lower.includes("running")) {
      bg = "#DBEAFE";
      text = "#1E40AF";
      border = "#BFDBFE";
      dot = "#2563EB";
    } else if (lower.includes("hold")) {
      bg = "#F5F3FF";
      text = "#6D28D9";
      border = "#DDD6FE";
      dot = "#8B5CF6";
    } else if (lower.includes("reject")) {
      bg = "#FEE2E2";
      text = "#991B1B";
      border = "#FECACA";
      dot = "#EF4444";
    }

    return (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-2xs"
        style={{
          backgroundColor: bg,
          color: text,
          border: `1px solid ${border}`,
          lineHeight: "1.2",
        }}
      >
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: dot,
            display: "inline-block",
            marginRight: "6px",
          }}
        />
        <span>{status}</span>
      </span>
    );
  };

  const renderRequirementCell = ({ value }) => (
    <div
      className="text-xs text-slate-700 whitespace-normal break-words max-w-[320px] line-clamp-2 leading-relaxed"
      title={value || ""}
    >
      {value || "-"}
    </div>
  );

  // --- Table Column Definitions with Support Dashboard Button Styles ---
  const PendingColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssingName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 250, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "AssignRequest",
      headerName: "ASSIGN REQUEST",
      width: 135,
      minWidth: 130,
      maxWidth: 145,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-assign shadow-xs"
          onClick={() => {
            setSelectedTaskId({ ...params.row, MainId: params.row.MainId });
            setIsJuniorModalOpen(true);
          }}
        >
          <UserPlus size={13} />
          <span>Assign Request</span>
        </button>
      ),
    },
    {
      field: "acceptTask",
      headerName: "ACCEPT TASK",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-accept shadow-xs"
          onClick={() => handleAcceptTask(params.row.MainId, params.row.AssingName)}
        >
          <CheckCircle2 size={13} />
          <span>Accept Task</span>
        </button>
      ),
    },
    {
      field: "editCallNo",
      headerName: "EDIT",
      width: 85,
      minWidth: 80,
      maxWidth: 95,
      renderCell: (params) => (
        <button
          type="button"
          className={`action-btn shadow-xs ${
            params.row.Status === "Pending"
              ? "action-btn-edit"
              : "action-btn-disabled"
          }`}
          onClick={() => {
            if (params.row.Status !== "Pending") {
              toast.error("Task has already been accepted and cannot be edited!");
              return;
            }
            setEditTaskId(params.row.MainId);
            setEditCallNo(params.row.tokan || "");
            setEditPresentStatus(params.row.PresentStatus || "");
            setEditRequirement(params.row.Requirement || "");
            setEditSolution(params.row.Solution || "");
            setEditBenefit(params.row.Benefit || "");
            setIsEditModalOpen(true);
          }}
        >
          <Pencil size={12} />
          <span>Edit</span>
        </button>
      ),
    },
  ];

  const PendingRows = useMemo(() => {
    return InhousRndData?.Panding?.map((el, index) => ({
      id: index + 1,
      tokan: el?.CallNo,
      MainId: el._id,
      CompanyName: el?.CompanyName?.WhaClientName,
      AssignDate: formatDate(el.Date),
      CompanyId: el.CompanyId?.CompanyName,
      AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
      AssingName: el.SuportTeamId?.Name || el.SupportTeamId?.Name,
      Requirement: el?.Requirement,
      PresentStatus: el?.PresentStatus,
      Solution: el?.Solution,
      Benefit: el?.Benefit,
      Status: el?.Status || "Pending",
    })) || [];
  }, [InhousRndData?.Panding]);

  const HoldingColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssingName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 220, renderCell: renderRequirementCell },
    {
      field: "TimeLimit",
      headerName: "TIME LIMIT",
      width: 110,
      minWidth: 105,
      maxWidth: 120,
      renderCell: (params) => <LiveTimeCell type="formattedTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "TaskWorkTime",
      headerName: "TASK WORK TIME",
      width: 150,
      minWidth: 140,
      maxWidth: 165,
      renderCell: (params) => <LiveTimeCell type="taskWorkTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "Days",
      headerName: "ESTIMATED DAYS",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => <LiveTimeCell type="calcDays" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "EstimatedDate",
      headerName: "ESTIMATED DATE",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => <LiveTimeCell type="estimatedDateStr" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={true} fallbackDate={params.row.rawDate} />
    },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "acceptTask",
      headerName: "ACCEPT TASK",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-accept shadow-xs"
          onClick={() => handleAcceptTask(params.row.MainId)}
        >
          <CheckCircle2 size={13} />
          <span>Accept Task</span>
        </button>
      ),
    },
    {
      field: "editCallNo",
      headerName: "EDIT",
      width: 85,
      minWidth: 80,
      maxWidth: 95,
      renderCell: (params) => (
        <button
          type="button"
          className={`action-btn shadow-xs ${
            params.row.Status === "Pending"
              ? "action-btn-edit"
              : "action-btn-disabled"
          }`}
          onClick={() => {
            if (params.row.Status !== "Pending") {
              toast.error("Task has already been accepted and cannot be edited!");
              return;
            }
            setEditTaskId(params.row.MainId);
            setEditCallNo(params.row.tokan || "");
            setEditPresentStatus(params.row.PresentStatus || "");
            setEditRequirement(params.row.Requirement || "");
            setEditSolution(params.row.Solution || "");
            setEditBenefit(params.row.Benefit || "");
            setIsEditModalOpen(true);
          }}
        >
          <Pencil size={12} />
          <span>Edit</span>
        </button>
      ),
    },
  ];

  const HoldingRows = useMemo(() => {
    return InhousRndData?.Hold?.map((el, index) => ({
      id: index + 1,
      tokan: el?.CallNo,
      MainId: el._id,
      CompanyName: el?.CompanyName?.WhaClientName,
      AssignDate: formatDate(el.Date),
      CompanyId: el.CompanyId?.CompanyName,
      AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
      AssingName: el.SuportTeamId?.Name || el.SupportTeamId?.Name,
      Requirement: el?.Requirement,
      rawTimeLimit: el?.TimeLimit,
      rawSlabRecords: el?.TimeSlabRecord,
      rawDate: el?.Date,
      PresentStatus: el?.PresentStatus,
      Solution: el?.Solution,
      Benefit: el?.Benefit,
      Status: el?.Status || "Hold",
    })) || [];
  }, [InhousRndData?.Hold]);

  const RunningColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssingName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    {
      field: "TimeLimit",
      headerName: "TIME LIMIT",
      width: 110,
      minWidth: 105,
      maxWidth: 120,
      renderCell: (params) => <LiveTimeCell type="formattedTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "TaskWorkTime",
      headerName: "TASK WORK TIME",
      width: 150,
      minWidth: 140,
      maxWidth: 165,
      renderCell: (params) => <LiveTimeCell type="taskWorkTime" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "Days",
      headerName: "ESTIMATED DAYS",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => <LiveTimeCell type="calcDays" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "EstimatedDate",
      headerName: "ESTIMATED DATE",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => <LiveTimeCell type="estimatedDateStr" timeLimitStr={params.row.rawTimeLimit} slabRecords={params.row.rawSlabRecords} isHold={false} fallbackDate={params.row.rawDate} />
    },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "holdTask",
      headerName: "HOLD TASK",
      width: 110,
      minWidth: 105,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-hold shadow-xs"
          onClick={() => handleHoldTask(params.row.MainId)}
        >
          <Clock size={13} />
          <span>Hold Task</span>
        </button>
      ),
    },
    {
      field: "AssignRequest",
      headerName: "ASSIGN REQUEST",
      width: 135,
      minWidth: 130,
      maxWidth: 145,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-assign shadow-xs"
          onClick={() => {
            setSelectedTaskId({ ...params.row, MainId: params.row.MainId });
            setIsJuniorModalOpen(true);
          }}
        >
          <UserPlus size={13} />
          <span>Assign Request</span>
        </button>
      ),
    },
    {
      field: "checkingTask",
      headerName: "CHECKING TASK",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-check shadow-xs"
          onClick={() => {
            handleStoreRecord(params.row.Record);
            setSelectedTaskId({ MainId: params.row.MainId });
            const defaultSeniorId = params.row.SeniorDeveloperId || "";
            setSelectedSeniorId(defaultSeniorId);
            setIsCheckTaskModalOpen(true);
          }}
        >
          <CheckCircle2 size={13} />
          <span>Check Task</span>
        </button>
      ),
    },
    {
      field: "editCallNo",
      headerName: "EDIT",
      width: 85,
      minWidth: 80,
      maxWidth: 95,
      renderCell: (params) => (
        <button
          type="button"
          className={`action-btn shadow-xs ${
            params.row.Status === "Pending"
              ? "action-btn-edit"
              : "action-btn-disabled"
          }`}
          onClick={() => {
            if (params.row.Status !== "Pending") {
              toast.error("Task has already been accepted and cannot be edited!");
              return;
            }
            setEditTaskId(params.row.MainId);
            setEditCallNo(params.row.tokan || "");
            setEditPresentStatus(params.row.PresentStatus || "");
            setEditRequirement(params.row.Requirement || "");
            setEditSolution(params.row.Solution || "");
            setEditBenefit(params.row.Benefit || "");
            setIsEditModalOpen(true);
          }}
        >
          <Pencil size={12} />
          <span>Edit</span>
        </button>
      ),
    },
  ];

  const RunningRows = useMemo(() => {
    return InhousRndData?.Accept?.map((el, index) => {
      const seniorId =
        el?.SeniorDevloperID?._id ||
        (typeof el?.SeniorDevloperID === "string" ? el?.SeniorDevloperID : "");
      return {
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignDate: formatDate(el.Date),
        CompanyId: el.CompanyId?.CompanyName,
        Record: el.Record,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        SeniorDeveloperId: seniorId,
        SeniorDeveloperObj: el?.SeniorDevloperID,
        AssingName: el.SuportTeamId?.Name || el.SupportTeamId?.Name,
        rawTimeLimit: el?.TimeLimit,
        rawSlabRecords: el?.TimeSlabRecord,
        rawDate: el?.Date,
        PresentStatus: el?.PresentStatus,
        Solution: el?.Solution,
        Benefit: el?.Benefit,
        Status: el?.Status || "Accept",
      };
    }) || [];
  }, [InhousRndData?.Accept]);

  const CompleteColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssingName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 300, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const CompleteRows = useMemo(() => {
    return InhousRndData?.Complete?.map((el, index) => ({
      id: index + 1,
      MainId: el._id,
      tokan: el?.CallNo,
      CompanyName: el?.CompanyName?.WhaClientName,
      AssignDate: formatDate(el.Date),
      CompanyId: el.CompanyId?.CompanyName,
      AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
      AssingName: el.SuportTeamId?.Name || el.SupportTeamId?.Name,
      Requirement: el?.Requirement,
      PresentStatus: el?.PresentStatus,
    })) || [];
  }, [InhousRndData?.Complete]);

  const JuniorToSeniorColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "AssignRequest",
      headerName: "ASSIGN REQUEST",
      width: 135,
      minWidth: 130,
      maxWidth: 145,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-assign shadow-xs"
          onClick={() => {
            setSelectedTaskId({ ...params.row, MainId: params.row.MainId });
            setIsJuniorModalOpen(true);
          }}
        >
          <UserPlus size={13} />
          <span>Assign Request</span>
        </button>
      ),
    },
    {
      field: "checkingTask",
      headerName: "CHECKING TASK",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-check shadow-xs"
          onClick={() => {
            handleStoreRecord(params.row.Record);
            setSelectedTaskId({ MainId: params.row.MainId });
            const defaultSeniorId = params.row.SeniorDeveloperId || "";
            setSelectedSeniorId(defaultSeniorId);
            setIsCheckTaskModalOpen(true);
          }}
        >
          <CheckCircle2 size={13} />
          <span>Check Task</span>
        </button>
      ),
    },

  ];

  const JuniorToSeniorRows = useMemo(() => {
    return InhousRndData?.Juniortosen?.map((el, index) => {
      const seniorId =
        el?.SeniorDevloperID?._id ||
        (typeof el?.SeniorDevloperID === "string" ? el?.SeniorDevloperID : "");
      return {
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignDate: formatDate(el.Date),
        CompanyId: el.CompanyId?.CompanyName,
        Record: el.Record,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        SeniorDeveloperId: seniorId,
        SeniorDeveloperObj: el?.SeniorDevloperID,
        PresentStatus: el?.PresentStatus,
      };
    }) || [];
  }, [InhousRndData?.Juniortosen]);

  const SeniorToJuniorColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "CallNo", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "SuportTeamId", headerName: "DEVELOPER NAME", minWidth: 175, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 115, minWidth: 110, maxWidth: 125, renderCell: renderStatusCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const SeniorToJuniorRows = useMemo(() => {
    return InhousRndData?.Junior?.map((el, index) => ({
      id: index + 1,
      MainId: el._id,
      CallNo: el?.CallNo,
      CompanyId: el.CompanyId?.CompanyName,
      CompanyName: el?.CompanyName?.WhaClientName,
      SuportTeamId: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
      Status: el?.Status,
      AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
      AssignDate: formatDate(el.Date),
      PresentStatus: el?.PresentStatus,
    })) || [];
  }, [InhousRndData?.Junior]);

  const CheckingToSupportColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssingName", headerName: "SUPPORT MEMBER", minWidth: 180, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 155, minWidth: 145, maxWidth: 175, renderCell: renderStatusCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 220, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const CheckingToSupportRows = useMemo(() => {
    return (
      InhousRndData?.CheckingToSupport?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssingName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        Status: el?.Status || "Checking to Support",
        Requirement: el?.Requirement,
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
        Record: el?.Record,
      })) || []
    );
  }, [InhousRndData?.CheckingToSupport]);

  const ForwardImplementationColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssingName", headerName: "ASSIGNED MEMBER", minWidth: 180, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 175, minWidth: 160, maxWidth: 195, renderCell: renderStatusCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 220, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "postCheckAction",
      headerName: "POST CHECK",
      width: 145,
      minWidth: 135,
      maxWidth: 160,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-check shadow-xs"
          onClick={() =>
            handleSendToPostCheck(
              params.row.MainId,
              params.row.tokan,
              params.row.AssignNameId,
              params.row.CodeUpdate
            )
          }
          title="Send for Checking after Implementation"
        >
          <CheckCircle2 size={13} />
          <span>Post-Check</span>
        </button>
      ),
    },
  ];

  const ForwardImplementationRows = useMemo(() => {
    const extractCodeUpdate = (el) => {
      if (el?.CodeUpdate && typeof el.CodeUpdate === "string" && el.CodeUpdate.trim()) {
        return el.CodeUpdate.trim();
      }
      if (Array.isArray(el?.Record)) {
        for (let i = el.Record.length - 1; i >= 0; i--) {
          const desc = el.Record[i]?.DescripTione || "";
          if (desc.includes("Code Update:")) {
            const parts = desc.split("Code Update:")[1];
            const text = parts ? parts.split("| Notes:")[0] : "";
            if (text && text.trim()) return text.trim();
          }
        }
      }
      return "";
    };

    return (
      InhousRndData?.ForwardImplementation?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssingName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignNameId: el?.SuportTeamId?._id || el?.SuportTeamId || el?.SupportTeamId?._id || el?.SupportTeamId || "",
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        Status: el?.Status || "Forward for Implementation",
        Requirement: el?.Requirement,
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
        Record: el?.Record,
        CodeUpdate: extractCodeUpdate(el),
      })) || []
    );
  }, [InhousRndData?.ForwardImplementation]);

  const CheckingAfterImplementationColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssingName", headerName: "SUPPORT MEMBER", minWidth: 180, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 185, minWidth: 170, maxWidth: 205, renderCell: renderStatusCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 250, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const CheckingAfterImplementationRows = useMemo(() => {
    return (
      InhousRndData?.CheckingAfterImplementation?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssingName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        Status: el?.Status || "Checking after Implementation",
        Requirement: el?.Requirement,
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
        Record: el?.Record,
      })) || []
    );
  }, [InhousRndData?.CheckingAfterImplementation]);

  if (
    !InhousRndData ||
    !DataFindSupport ||
    !JuniorDevelopers ||
    !InhousRndData?.Panding
  ) {
    return <Loader />;
  }

  return (
    <>
      <div className="w-full dashboard-page-container">
        {/* Support Dashboard Compact Banner with Clock & Badge */}
        <div className="page-header-banner-compact">
          <div>
            <h4 className="mb-0 font-bold">Inhouse R&D Dashboard</h4>
            <span className="page-subtitle">
              Track, assign, and resolve inhouse R&D tasks and developer assignments
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Live Current Date & Time Clock */}
            <div className="dashboard-live-clock" title="Current Live Date & Time">
              <span className="clock-date">
                <Calendar size={14} className="text-blue-600" />
                <span>{formatLiveDate(currentDateTime)}</span>
              </span>
              <span className="clock-divider"></span>
              <span className="clock-time">
                <Clock size={14} className="text-blue-600" />
                <span>{formatLiveTime(currentDateTime)}</span>
                <span className="live-dot" title="Live Clock Running"></span>
              </span>
            </div>

            {/* Total Pending Count Badge */}
            <span
              className="px-2.5 py-1.5 font-bold text-xs rounded-lg shadow-xs flex items-center gap-1.5 bg-amber-400 text-amber-950"
            >
              Pending: {PendingRows.length}
            </span>
          </div>
        </div>

        {/* Status Tabs Bar */}
        <div className="dashboard-tabs-bar">
          <button
            type="button"
            className={`tab-btn-status tab-btn-queue ${activeTab === "queue" ? "active" : ""}`}
            onClick={() => setActiveTab("queue")}
          >
            <span className="tab-status-dot"></span>
            <span>In Queue Task</span>
            <span className="tab-badge">{PendingRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-holding ${activeTab === "holding" ? "active" : ""}`}
            onClick={() => setActiveTab("holding")}
          >
            <span className="tab-status-dot"></span>
            <span>Holding Task</span>
            <span className="tab-badge">{HoldingRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-running ${activeTab === "running" ? "active" : ""}`}
            onClick={() => setActiveTab("running")}
          >
            <span className="tab-status-dot"></span>
            <span>Running Task</span>
            <span className="tab-badge">{RunningRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-complete ${activeTab === "complete" ? "active" : ""}`}
            onClick={() => setActiveTab("complete")}
          >
            <span className="tab-status-dot"></span>
            <span>Complete Task</span>
            <span className="tab-badge">{CompleteRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-juniorsenior ${activeTab === "juniorsenior" ? "active" : ""}`}
            onClick={() => setActiveTab("juniorsenior")}
          >
            <span className="tab-status-dot"></span>
            <span>Junior To Senior</span>
            <span className="tab-badge">{JuniorToSeniorRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-seniorjunior ${activeTab === "seniorjunior" ? "active" : ""}`}
            onClick={() => setActiveTab("seniorjunior")}
          >
            <span className="tab-status-dot"></span>
            <span>Senior To Junior</span>
            <span className="tab-badge">{SeniorToJuniorRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-checksupport ${activeTab === "checksupport" ? "active" : ""}`}
            onClick={() => setActiveTab("checksupport")}
          >
            <span className="tab-status-dot"></span>
            <span>Checking to Support</span>
            <span className="tab-badge">{CheckingToSupportRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-forwardimpl ${activeTab === "forwardimpl" ? "active" : ""}`}
            onClick={() => setActiveTab("forwardimpl")}
          >
            <span className="tab-status-dot"></span>
            <span>Forward for Implementation</span>
            <span className="tab-badge">{ForwardImplementationRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-checkafterimpl ${activeTab === "checkafterimpl" ? "active" : ""}`}
            onClick={() => setActiveTab("checkafterimpl")}
          >
            <span className="tab-status-dot"></span>
            <span>Checking after Implementation</span>
            <span className="tab-badge">{CheckingAfterImplementationRows.length}</span>
          </button>

        </div>

        {/* Main Table Area Filling Remaining Screen Height */}
        <div className="w-full flex-grow flex flex-col min-h-0 overflow-hidden">
          {/* Tab 1: In Queue Task */}
          {activeTab === "queue" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #f59e0b" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-amber-600">
                    In Queue Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    New pending requests awaiting developer assignment or pickup
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {PendingRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_queue"
                  fileName="Inhouse_InQueue_Tasks"
                  columns={PendingColumns}
                  rows={PendingRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No in-queue tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 2: Holding Task */}
          {activeTab === "holding" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #6366f1" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-indigo-600">
                    Holding Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks placed on temporary hold with timer pause
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {HoldingRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_holding"
                  fileName="Inhouse_Holding_Tasks"
                  columns={HoldingColumns}
                  rows={HoldingRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No holding tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 3: Running Task */}
          {activeTab === "running" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #1d4ed8" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-blue-600">
                    Running Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Currently active tasks with running live countdown timer
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {RunningRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_running"
                  fileName="Inhouse_Running_Tasks"
                  columns={RunningColumns}
                  rows={RunningRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No running tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 4: Complete Task */}
          {activeTab === "complete" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #15803d" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-emerald-600">
                    Complete Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Successfully finished and closed inhouse R&D tasks
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {CompleteRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_complete"
                  fileName="Inhouse_Complete_Tasks"
                  columns={CompleteColumns}
                  rows={CompleteRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No completed tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 5: Junior To Senior Task List */}
          {activeTab === "juniorsenior" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #7e22ce" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-purple-600">
                    Junior To Senior Task List
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks submitted by junior developers awaiting senior developer verification
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {JuniorToSeniorRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_juniorsenior"
                  fileName="Inhouse_JuniorToSenior_Tasks"
                  columns={JuniorToSeniorColumns}
                  rows={JuniorToSeniorRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No junior to senior tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 6: Senior To Junior Task List */}
          {activeTab === "seniorjunior" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #0d9488" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-teal-600">
                    Senior To Junior Task List
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks delegated by seniors to junior developers with time allocation
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {SeniorToJuniorRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_seniorjunior"
                  fileName="Inhouse_SeniorToJunior_Tasks"
                  columns={SeniorToJuniorColumns}
                  rows={SeniorToJuniorRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No senior to junior tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 7: Checking to Support */}
          {activeTab === "checksupport" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #0284c7" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-sky-600">
                    Checking to Support
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks reviewed by senior and awaiting support verification
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {CheckingToSupportRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_checksupport"
                  fileName="Inhouse_CheckingToSupport_Tasks"
                  columns={CheckingToSupportColumns}
                  rows={CheckingToSupportRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No checking to support tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 8: Forward for Implementation */}
          {activeTab === "forwardimpl" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #7e22ce" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-purple-600">
                    Forward for Implementation
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks currently deployed for client server/EXE implementation
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {ForwardImplementationRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_forwardimpl"
                  fileName="Inhouse_Forward_Implementation_Tasks"
                  columns={ForwardImplementationColumns}
                  rows={ForwardImplementationRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No tasks in forward for implementation."
                />
              </div>
            </div>
          )}

          {/* Tab 9: Checking after Implementation */}
          {activeTab === "checkafterimpl" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #0891b2" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-cyan-600">
                    Checking after Implementation
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks with implementation finished, under final post-check before completion
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {CheckingAfterImplementationRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="inhouse_checkafterimpl"
                  fileName="Inhouse_CheckingAfterImplementation_Tasks"
                  columns={CheckingAfterImplementationColumns}
                  rows={CheckingAfterImplementationRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No tasks in checking after implementation."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Supporting Person */}
      {isModalOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-md p-6 border border-slate-100">
            <h2 className="text-lg font-bold mb-4 text-[#112A47]">
              Select Supporting Person
            </h2>

            {recordData?.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-3.5 mb-4 bg-slate-50/50">
                <h3 className="text-sm font-bold text-gray-700 mb-2.5">
                  Developers Involved
                </h3>

                <div className="space-y-2.5">
                  {recordData.map((dev, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border-b border-gray-200/70 pb-2"
                    >
                      <div>
                        <p className="text-gray-800 font-semibold text-xs">
                          {dev.DeveloperName}
                        </p>
                      </div>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <span
                            key={i}
                            onClick={() => {
                              setRecordData((prev) => {
                                const currentTotal = prev.reduce(
                                  (sum, d) => sum + (d.rating || 0),
                                  0
                                );
                                const currentRating = prev[index].rating || 0;
                                const newRating = i + 1;
                                const totalAfterChange =
                                  currentTotal - currentRating + newRating;

                                if (totalAfterChange > 10) {
                                  toast.warn("Total rating cannot exceed 10!");
                                  return prev;
                                }

                                return prev.map((d, idx) =>
                                  idx === index
                                    ? { ...d, rating: newRating }
                                    : d
                                );
                              });
                            }}
                            className={`cursor-pointer text-lg ${i < (dev.rating || 0)
                              ? "text-amber-400"
                              : "text-gray-300"
                              }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="ml-1.5 text-xs font-semibold text-gray-600">
                          {dev.rating || 0}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-2.5 text-right text-xs text-gray-700">
                  Total Rating:{" "}
                  <span className="font-bold text-[#112A47]">
                    {recordData.reduce((sum, d) => sum + (d.rating || 0), 0)}
                    /10
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2.5">
              <button
                type="button"
                className="px-4 py-1.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedTaskId(null);
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className="px-5 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm transition"
                onClick={() => {
                  const loginData = JSON.parse(Cookies.get("loginData") || "{}");
                  const user = loginData?.data;
                  let finalRatings = [];

                  if (recordData?.length > 0) {
                    const total = recordData.reduce(
                      (sum, d) => sum + (d.rating || 0),
                      0
                    );

                    if (total < 10) {
                      const diff = 10 - total;
                      const updated = [...recordData];
                      updated[updated.length - 1].rating =
                        (updated[updated.length - 1].rating || 0) + diff;
                      finalRatings = updated;
                      toast.info(`Auto-adjusted ratings to reach total of 10.`);
                    } else if (total > 10) {
                      toast.error("Total rating cannot exceed 10!");
                      return;
                    } else {
                      finalRatings = recordData;
                    }
                  } else {
                    finalRatings = [
                      {
                        DeveloperID: user?._id,
                        DeveloperName:
                          user?.Name || user?.username || "Unknown User",
                        rating: 10,
                      },
                    ];
                  }

                  toast.info("Completing task...", { autoClose: 1500 });

                  dispatch(
                    InhouseRNDCompleteTask({
                      complainBoxId: selectedTaskId?.MainId,
                      ratings: finalRatings,
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task completed successfully!");
                      const userId = user?._id;
                      setTimeout(() => {
                        dispatch(fetchInhouseRndData(userId));
                      }, 100);
                    } else {
                      toast.error("Failed to complete task. Please try again.");
                    }
                  }).catch(() => {
                    toast.error("An error occurred while completing task.");
                  });

                  setIsModalOpen(false);
                  setSelectedTaskId(null);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body)}

      {/* Modal for Junior → Senior Assignment */}
      {isJuniorToSeniorModalOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-md p-6 border border-slate-100">
            <h2 className="text-lg font-bold mb-4 text-[#112A47]">
              Assign Task to Senior Developer
            </h2>

            {recordData?.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-3.5 mb-4 bg-slate-50/50">
                <h3 className="text-sm font-bold text-gray-700 mb-2.5">
                  Developers Involved
                </h3>

                <div className="space-y-2.5">
                  {recordData.map((dev, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border-b border-gray-200/70 pb-2"
                    >
                      <div>
                        <p className="text-gray-800 font-semibold text-xs">
                          {dev.DeveloperName}
                        </p>
                      </div>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <span
                            key={i}
                            onClick={() => {
                              setRecordData((prev) => {
                                const currentTotal = prev.reduce(
                                  (sum, d) => sum + (d.rating || 0),
                                  0
                                );
                                const currentRating = prev[index].rating || 0;
                                const newRating = i + 1;
                                const totalAfterChange =
                                  currentTotal - currentRating + newRating;

                                if (totalAfterChange > 10) {
                                  toast.warn("Total rating cannot exceed 10!");
                                  return prev;
                                }

                                return prev.map((d, idx) =>
                                  idx === index
                                    ? { ...d, rating: newRating }
                                    : d
                                );
                              });
                            }}
                            className={`cursor-pointer text-lg ${i < (dev.rating || 0)
                              ? "text-amber-400"
                              : "text-gray-300"
                              }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="ml-1.5 text-xs font-semibold text-gray-600">
                          {dev.rating || 0}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-2.5 text-right text-xs text-gray-700">
                  Total Rating:{" "}
                  <span className="font-bold text-[#112A47]">
                    {recordData.reduce((sum, d) => sum + (d.rating || 0), 0)}
                    /10
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2.5">
              <button
                type="button"
                className="px-4 py-1.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                onClick={() => {
                  setIsJuniorToSeniorModalOpen(false);
                  setSelectedTaskId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-5 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  recordData.reduce((sum, d) => sum + (d.rating || 0), 0) !==
                  10
                }
                onClick={() => {
                  const total = recordData.reduce(
                    (sum, d) => sum + (d.rating || 0),
                    0
                  );

                  if (total !== 10) {
                    toast.error("Please ensure the total rating equals exactly 10 before proceeding!");
                    return;
                  }

                  toast.info("Completing task...", { autoClose: 1500 });

                  dispatch(
                    InhouseRNDCompleteTask({
                      complainBoxId: selectedTaskId.MainId,
                      ratings: recordData,
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task completed successfully!");
                      const loginData = JSON.parse(Cookies.get("loginData") || "{}");
                      const userId = loginData?.data?._id;
                      setTimeout(() => {
                        dispatch(fetchInhouseRndData(userId));
                      }, 100);
                    } else {
                      toast.error("Failed to complete task. Please try again.");
                    }
                  }).catch(() => {
                    toast.error("An error occurred while completing task.");
                  });

                  setIsJuniorToSeniorModalOpen(false);
                  setSelectedTaskId(null);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body)}

      {/* Modal for Check Task */}
      {isCheckTaskModalOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-md p-6 border border-slate-100">
            <h2 className="text-lg font-bold mb-4 text-[#112A47]">
              Check Task (Senior Review)
            </h2>

            <div className="mb-3">
              <SearchableDropdown
                options={SeniorOptions}
                value={selectedSeniorId}
                onChange={(id) => setSelectedSeniorId(id)}
                placeholder="Select Developer / Support Member"
              />
            </div>

            <div className="mb-3">
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Remarks / Notes</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter your remarks or comments..."
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 outline-none resize-none transition"
                rows={3}
              ></textarea>
            </div>

            {recordData?.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-3.5 mb-4 bg-slate-50/50">
                <h3 className="text-sm font-bold text-gray-700 mb-2.5">
                  Developers Involved
                </h3>

                <div className="space-y-2.5">
                  {recordData.map((dev, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border-b border-gray-200/70 pb-2"
                    >
                      <div>
                        <p className="text-gray-800 font-semibold text-xs">
                          {dev.DeveloperName}
                        </p>
                      </div>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <span
                            key={i}
                            onClick={() => {
                              setRecordData((prev) => {
                                const currentTotal = prev.reduce(
                                  (sum, d) => sum + (d.rating || 0),
                                  0
                                );
                                const currentRating = prev[index].rating || 0;
                                const newRating = i + 1;
                                const totalAfterChange =
                                  currentTotal - currentRating + newRating;

                                if (totalAfterChange > 10) {
                                  toast.warn("Total rating cannot exceed 10!");
                                  return prev;
                                }

                                return prev.map((d, idx) =>
                                  idx === index
                                    ? { ...d, rating: newRating }
                                    : d
                                );
                              });
                            }}
                            className={`cursor-pointer text-lg ${i < (dev.rating || 0)
                              ? "text-amber-400"
                              : "text-gray-300"
                              }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="ml-1.5 text-xs font-semibold text-gray-600">
                          {dev.rating || 0}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-2.5 text-right text-xs text-gray-700">
                  Total Rating:{" "}
                  <span className="font-bold text-[#112A47]">
                    {recordData.reduce((sum, d) => sum + (d.rating || 0), 0)}
                    /10
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2.5">
              <button
                type="button"
                className="px-4 py-1.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                onClick={() => {
                  setIsCheckTaskModalOpen(false);
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className="px-5 py-1.5 text-xs font-semibold bg-sky-600 text-white rounded-lg hover:bg-sky-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  recordData.reduce((sum, d) => sum + (d.rating || 0), 0) !==
                  10
                }
                onClick={() => {
                  const total = recordData.reduce(
                    (sum, d) => sum + (d.rating || 0),
                    0
                  );

                  if (total !== 10) {
                    toast.error("Please ensure the total rating equals exactly 10 before proceeding!");
                    return;
                  }

                  if (!selectedSeniorId) {
                    toast.error("Please select a Senior Developer.");
                    return;
                  }

                  toast.info("Assigning task to senior...", { autoClose: 1500 });

                  dispatch(
                    JuniorToSeniorAssignRatingReq({
                      complainBoxId: selectedTaskId?.MainId || selectedTaskId?._id,
                      SuportTeamId: selectedSeniorId,
                      SupportTeamId: selectedSeniorId,
                      ratings: recordData,
                      SeniorDevloperID: loggedInUserId,
                      DescripTione: description,
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task assigned to senior successfully!");
                      if (loggedInUserId) {
                        dispatch(fetchInhouseRndData(loggedInUserId));
                      }
                    } else {
                      toast.error("Failed to assign task to senior.");
                    }
                  }).catch(() => {
                    toast.error("An error occurred while assigning task.");
                  });

                  setIsCheckTaskModalOpen(false);
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body)}

      {/* Modal for Junior Developer Assignment */}
      {isJuniorModalOpen && typeof document !== "undefined" && createPortal((() => {
        const currentTaskDetails = (selectedTaskId?.tokan || selectedTaskId?.CallNo)
          ? selectedTaskId
          : PendingRows.find((r) => r.MainId === selectedTaskId?.MainId) ||
          RunningRows.find((r) => r.MainId === selectedTaskId?.MainId) ||
          HoldingRows.find((r) => r.MainId === selectedTaskId?.MainId) ||
          CompleteRows.find((r) => r.MainId === selectedTaskId?.MainId) ||
          SeniorToJuniorRows.find((r) => r.MainId === selectedTaskId?.MainId) ||
          JuniorToSeniorRows.find((r) => r.MainId === selectedTaskId?.MainId);

        const callNumber =
          currentTaskDetails?.tokan ||
          currentTaskDetails?.CallNo ||
          currentTaskDetails?.ComplainBoxId?.CallNo;

        return (
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200 overflow-y-auto"
            onClick={() => {
              setIsJuniorModalOpen(false);
              setSelectedJuniorId("");
              setSelectedTaskId(null);
              setDescription("");
              setTimeLimitHours("");
              setTimeLimitMinutes("");
              setMinutesError("");
            }}
          >
            <div
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-100 flex flex-col overflow-visible animate-in fade-in zoom-in-95 duration-200 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-3.5 bg-gradient-to-r from-[#112A47] to-[#1c3a60] text-white flex items-center justify-between shadow-sm rounded-t-2xl">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center text-white border border-white/15 shadow-inner">
                    <UserPlus size={18} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold tracking-wide text-white leading-tight">
                      Select Junior Developer
                    </h2>
                    <p className="text-[11px] text-blue-200/90 font-normal">
                      Assign task and set allocated time limit
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsJuniorModalOpen(false);
                    setSelectedJuniorId("");
                    setSelectedTaskId(null);
                    setDescription("");
                    setTimeLimitHours("");
                    setTimeLimitMinutes("");
                    setMinutesError("");
                  }}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors focus:outline-none"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-3">
                {currentTaskDetails && (
                  <div className="p-2.5 bg-blue-50/60 border border-blue-100 rounded-xl space-y-0.5 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-[#112A47] text-xs">
                        Call #{callNumber || "—"}
                      </span>
                      <span className="font-medium text-slate-700 bg-white px-2 py-0.5 rounded-md border border-blue-100 text-[11px] truncate max-w-[200px]">
                        {currentTaskDetails.CompanyName || currentTaskDetails.ComplainBoxId?.CompanyId?.CompanyName || "Dynamic Softlink"}
                      </span>
                    </div>
                    {(currentTaskDetails.Requirement || currentTaskDetails.ComplainBoxId?.TaskRequirements) && (
                      <p className="text-slate-600 text-[11px] line-clamp-2 pt-0.5 border-t border-blue-100/70">
                        <span className="font-semibold text-slate-700">Req: </span>
                        {currentTaskDetails.Requirement || currentTaskDetails.ComplainBoxId?.TaskRequirements}
                      </p>
                    )}
                  </div>
                )}

                <SearchableDropdown
                  options={juniorOptions}
                  value={selectedJuniorId}
                  onChange={(id) => setSelectedJuniorId(id)}
                  placeholder="Junior Developer"
                />

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                    <span>Instructions / Description</span>
                    <span className="text-slate-400 font-normal text-[11px]">(Optional)</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter instructions or notes for junior developer..."
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-200 rounded-xl focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-none placeholder:text-slate-400"
                    rows={2}
                  ></textarea>
                </div>

                <div className="p-2.5 bg-slate-50 border border-slate-200/80 rounded-xl space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                      <Clock size={14} className="text-[#112A47]" />
                      <span>Estimated Time Limit</span>
                    </div>
                    <span className="text-slate-400 text-[11px] font-normal">(HH : MM)</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 mb-0.5 block">Hours</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={timeLimitHours}
                          onChange={(e) => setTimeLimitHours(e.target.value)}
                          placeholder="e.g. 02"
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-300"
                          min="0"
                        />
                        <span className="absolute right-2.5 top-1.5 text-[11px] text-slate-400 pointer-events-none">hrs</span>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-medium text-slate-500 mb-0.5 block">Minutes</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={timeLimitMinutes}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (val.length > 2) val = val.slice(0, 2);
                            if (parseInt(val || "0", 10) > 60) {
                              setMinutesError("Minutes cannot be more than 60");
                            } else {
                              setMinutesError("");
                            }
                            setTimeLimitMinutes(val);
                          }}
                          placeholder="e.g. 30"
                          className={`w-full px-2.5 py-1.5 text-xs bg-white border rounded-lg focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-300 ${minutesError ? "border-red-500 ring-1 ring-red-200" : "border-slate-200"
                            }`}
                          min="0"
                          max="59"
                        />
                        <span className="absolute right-2.5 top-1.5 text-[11px] text-slate-400 pointer-events-none">min</span>
                      </div>
                      {minutesError && (
                        <p className="text-red-500 text-[10px] mt-0.5 font-medium">{minutesError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/80 flex items-center justify-end gap-2.5 rounded-b-2xl">
                <button
                  type="button"
                  className="px-4 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition-all"
                  onClick={() => {
                    setIsJuniorModalOpen(false);
                    setSelectedJuniorId("");
                    setSelectedTaskId(null);
                    setDescription("");
                    setTimeLimitHours("");
                    setTimeLimitMinutes("");
                    setMinutesError("");
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="px-5 py-1.5 text-xs font-semibold text-white bg-[#112A47] hover:bg-[#0c2545] rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  disabled={!selectedJuniorId}
                  onClick={() => {
                    if (parseInt(timeLimitMinutes || "0", 10) >= 60) {
                      toast.error("Please enter valid minutes (less than 60).", { autoClose: 2000 });
                      return;
                    }

                    const seniorId = loggedInUserId;

                    toast.info("Assigning task to junior...", { autoClose: 1500 });

                    dispatch(
                      SeniortoJuniorAssignReq({
                        complainBoxId: selectedTaskId?.MainId || selectedTaskId?._id,
                        SuportTeamId: selectedJuniorId,
                        SupportTeamId: selectedJuniorId,
                        DescripTione: description,
                        SeniorDevloperID: seniorId,
                        TimeLimit:
                          !timeLimitHours && !timeLimitMinutes
                            ? null
                            : `${(timeLimitHours || "00").toString().padStart(2, "0")}:${(timeLimitMinutes || "00").toString().padStart(2, "0")}`,
                      })
                    )
                      .then((res) => {
                        if (res.meta.requestStatus === "fulfilled") {
                          toast.success("Task assigned to junior successfully!");
                          setTimeout(() => {
                            if (seniorId) {
                              dispatch(fetchInhouseRndData(seniorId));
                            }
                          }, 100);
                        } else {
                          toast.error("Failed to assign task to junior.");
                        }
                      })
                      .catch(() => {
                        toast.error("An error occurred while assigning task.");
                      });

                    setIsJuniorModalOpen(false);
                    setSelectedJuniorId("");
                    setSelectedTaskId(null);
                    setDescription("");
                    setTimeLimitHours("");
                    setTimeLimitMinutes("");
                    setMinutesError("");
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span>Assign Task</span>
                </button>
              </div>
            </div>
          </div>
        );
      })(), document.body)}

      {/* Modal for Edit Task Details */}
      {isEditModalOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4">
          <div className="bg-white rounded-xl shadow-2xl w-[95%] max-w-xl p-6 my-8 border border-slate-100">
            <h2 className="text-lg font-bold mb-4 text-[#112A47] border-b border-slate-100 pb-2">
              Edit Task Details {editCallNo && <span className="text-blue-600 font-mono">(#{editCallNo})</span>}
            </h2>

            <div className="space-y-3.5 max-h-[60vh] overflow-y-auto pr-1">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-gray-700">Present Status</label>
                <textarea
                  rows={2}
                  value={editPresentStatus}
                  onChange={(e) => setEditPresentStatus(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-xs focus:border-[#112A47] focus:ring-1 focus:ring-blue-500/20 outline-none resize-y w-full transition"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-gray-700">Requirement</label>
                <textarea
                  rows={2}
                  value={editRequirement}
                  onChange={(e) => setEditRequirement(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-xs focus:border-[#112A47] focus:ring-1 focus:ring-blue-500/20 outline-none resize-y w-full transition"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-gray-700">Solution</label>
                <textarea
                  rows={2}
                  value={editSolution}
                  onChange={(e) => setEditSolution(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-xs focus:border-[#112A47] focus:ring-1 focus:ring-blue-500/20 outline-none resize-y w-full transition"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-gray-700">Benefits</label>
                <textarea
                  rows={2}
                  value={editBenefit}
                  onChange={(e) => setEditBenefit(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-xs focus:border-[#112A47] focus:ring-1 focus:ring-blue-500/20 outline-none resize-y w-full transition"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2.5 mt-5 border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-1.5 border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="px-5 py-1.5 bg-[#112A47] text-white rounded-lg text-xs font-semibold hover:bg-[#0c2545] shadow-sm transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>,
        document.body)}

      {/* Modal for Post-Check / Checking after Implementation */}
      {isPostCheckModalOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => {
            setIsPostCheckModalOpen(false);
            setSelectedPostCheckMemberId("");
            setPostCheckTaskId(null);
            setPostCheckDescription("");
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md border border-slate-100 overflow-visible transform transition-all animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-3.5 bg-gradient-to-r from-[#0284c7] to-[#0369a1] text-white flex items-center justify-between shadow-sm rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center text-white border border-white/15 shadow-inner">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold tracking-wide text-white leading-tight">
                    Checking after Implementation
                  </h2>
                  <p className="text-[11px] text-sky-100/90 font-normal">
                    {postCheckTaskToken ? `Token No: #${postCheckTaskToken}` : "Assign verification to team member"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsPostCheckModalOpen(false);
                  setSelectedPostCheckMemberId("");
                  setPostCheckTaskId(null);
                  setPostCheckDescription("");
                }}
                className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors focus:outline-none"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Select Member for Verification <span className="text-rose-500">*</span>
                </label>
                <SearchableDropdown
                  options={postCheckMemberOptions}
                  value={selectedPostCheckMemberId}
                  onChange={(id) => setSelectedPostCheckMemberId(id)}
                  placeholder="Choose member..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Instructions / Notes
                  </label>
                  <span className="text-[11px] text-slate-500 font-normal">
                    (Optional)
                  </span>
                </div>
                <textarea
                  value={postCheckDescription}
                  onChange={(e) => setPostCheckDescription(e.target.value)}
                  placeholder="Enter notes for checking (e.g. EXE updated, please test)..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0284c7]/20 focus:border-[#0284c7] transition-all resize-none placeholder:text-slate-400 text-slate-700"
                  rows={2}
                ></textarea>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    What is updated in this code? <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <span className="text-[11px] text-rose-500 font-semibold">
                    (Compulsory)
                  </span>
                </div>
                <textarea
                  value={postCheckCodeUpdate}
                  onChange={(e) => setPostCheckCodeUpdate(e.target.value)}
                  placeholder="Enter what changes/updates were made in code (Required)..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0284c7]/20 focus:border-[#0284c7] transition-all resize-none placeholder:text-slate-400 text-slate-700 font-medium"
                  rows={3}
                  required
                ></textarea>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2.5 rounded-b-2xl">
              <button
                type="button"
                onClick={() => {
                  setIsPostCheckModalOpen(false);
                  setSelectedPostCheckMemberId("");
                  setPostCheckTaskId(null);
                  setPostCheckDescription("");
                  setPostCheckCodeUpdate("");
                }}
                className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 font-medium text-xs transition-colors focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedPostCheckMemberId || !postCheckCodeUpdate.trim()}
                className="px-4 py-2 rounded-xl bg-[#0284c7] hover:bg-[#0369a1] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs shadow-md shadow-sky-900/10 transition-all focus:outline-none flex items-center gap-1.5"
                onClick={() => {
                  if (!selectedPostCheckMemberId || !postCheckTaskId) return;
                  if (!postCheckCodeUpdate.trim()) {
                    toast.error("Please enter what is updated in this code!");
                    return;
                  }
                  dispatch(
                    checkingAfterImplementationReq({
                      id: postCheckTaskId,
                      SuportTeamId: selectedPostCheckMemberId,
                      codeUpdate: postCheckCodeUpdate.trim(),
                      description: postCheckDescription || "",
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task moved to Checking after Implementation successfully!");
                      if (loggedInUserId) {
                        dispatch(fetchInhouseRndData(loggedInUserId));
                      }
                    } else {
                      toast.error(res.payload || "Failed to update task stage.");
                    }
                  }).catch(() => {
                    toast.error("An error occurred while updating stage.");
                  });

                  setIsPostCheckModalOpen(false);
                  setSelectedPostCheckMemberId("");
                  setPostCheckTaskId(null);
                  setPostCheckDescription("");
                  setPostCheckCodeUpdate("");
                }}
              >
                <CheckCircle2 size={14} />
                <span>Assign & Send</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default InhouseRnd;
